import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getUserTimesheet, updateUser, deleteUser,
} from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft, Clock, Receipt, Save, Trash2, MapPin, AlertTriangle,
  TrendingUp, Calendar, Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

import { formatDate, formatDateTime } from '../../utils/date';

const fmtMoney = (v) => `£${(Number(v) || 0).toLocaleString('en-GB', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const fmtDate = (iso) => formatDate(iso);
const fmtDateTime = (iso) => formatDateTime(iso);

const EngineerProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    return getUserTimesheet(userId)
      .then(d => {
        setData(d);
        setEditing({
          name: d.user.name || '',
          email: d.user.email || '',
          phone: d.user.phone || '',
          title: d.user.title || '',
          rate: d.user.rate || '',
        });
      })
      .catch(() => toast.error('Failed to load engineer profile'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, [userId]);

  const dirty = useMemo(() => {
    if (!data || !editing) return false;
    const u = data.user;
    return (editing.name !== (u.name || '')) || (editing.email !== (u.email || ''))
      || (editing.phone !== (u.phone || '')) || (editing.title !== (u.title || ''))
      || (Number(editing.rate || 0) !== Number(u.rate || 0));
  }, [data, editing]);

  const save = async () => {
    setSaving(true);
    try {
      await updateUser(userId, {
        name: editing.name, email: editing.email,
        phone: editing.phone, title: editing.title,
        rate: editing.rate === '' ? null : Number(editing.rate),
      });
      toast.success('Saved');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete user "${data.user.name}"? This cannot be undone.`)) return;
    try {
      await deleteUser(userId);
      toast.success('Deleted');
      navigate('/admin/users');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading || !data) return <div className="p-6 text-slate-500">Loading engineer profile…</div>;
  const { user, totalClockInHours, totalWorkOrderHours, totalWorkOrderValue, weekly, clockIns, workOrderLabor } = data;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="engineer-profile-page">
      <Link to="/admin/users" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to users
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl text-slate-900">{user.name}</h1>
              <Badge variant="outline" className="capitalize">{user.role}</Badge>
              {user.title && <Badge variant="outline" className="text-slate-500">{user.title}</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-1 space-x-3">
              {user.email && <span>{user.email}</span>}
              {user.phone && <span>{user.phone}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={remove} data-testid="engineer-delete-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
            <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid="engineer-save-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {/* KPI rollup */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <Kpi
            label="Clock-in hours"
            value={`${totalClockInHours.toFixed(1)}h`}
            sub="Total time on site, last 30 days"
            testId="kpi-clockin"
          />
          <Kpi
            label="WO labour hours"
            value={`${totalWorkOrderHours.toFixed(1)}h`}
            sub={`${workOrderLabor.length} entries`}
            testId="kpi-wo-hours"
          />
          <Kpi
            label="WO labour value"
            value={fmtMoney(totalWorkOrderValue)}
            sub="Billable to customers"
            testId="kpi-wo-value"
          />
        </div>

        {/* Weekly hours chart — last 12 weeks of clock-in time */}
        {weekly.length > 0 && (
          <div className="mt-6 pt-5 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-slate-500" />
              <div className="text-xs uppercase tracking-wider text-slate-500">Weekly hours (last 12 weeks)</div>
            </div>
            <div className="h-40 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekly} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <XAxis
                    dataKey="weekStart"
                    tickFormatter={(d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(v) => `${v}h`}
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    axisLine={false} tickLine={false}
                    width={32}
                  />
                  <Tooltip
                    formatter={(v) => [`${v}h`, 'Hours']}
                    labelFormatter={(d) => `w/c ${new Date(d).toLocaleDateString('en-GB')}`}
                    contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="hours" radius={[3, 3, 0, 0]}>
                    {weekly.map((w, i) => (
                      <Cell key={i} fill={i === weekly.length - 1 ? '#2563eb' : '#94a3b8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="clockins">
        <TabsList>
          <TabsTrigger value="clockins" data-testid="tab-clockins">
            <Clock className="h-4 w-4 mr-1.5" /> Clock-ins ({clockIns.length})
          </TabsTrigger>
          <TabsTrigger value="wo-labor" data-testid="tab-wo-labor">
            <Receipt className="h-4 w-4 mr-1.5" /> WO labour ({workOrderLabor.length})
          </TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">
            <Briefcase className="h-4 w-4 mr-1.5" /> Profile
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clockins" className="pt-4">
          <ClockInList rows={clockIns} />
        </TabsContent>
        <TabsContent value="wo-labor" className="pt-4">
          <WorkOrderLabourList rows={workOrderLabor} />
        </TabsContent>
        <TabsContent value="profile" className="pt-4">
          <ProfileEditor editing={editing} setEditing={setEditing} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Kpi = ({ label, value, sub, testId }) => (
  <div className="bg-slate-50 border border-slate-200 rounded p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-display text-2xl tabular-nums mt-1 text-slate-900">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const ClockInList = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
        <Clock className="h-8 w-8 mx-auto text-slate-300 mb-2" />
        <div className="text-sm text-slate-500">No clock-ins yet.</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-4 py-2.5">Date</th>
            <th className="text-left px-4 py-2.5 hidden sm:table-cell">In</th>
            <th className="text-left px-4 py-2.5 hidden sm:table-cell">Out</th>
            <th className="text-right px-4 py-2.5">Hours</th>
            <th className="text-left px-4 py-2.5 hidden md:table-cell">Work order</th>
            <th className="text-left px-4 py-2.5 hidden md:table-cell">Geo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map(r => (
            <tr key={r.id} data-testid={`clockin-row-${r.id}`}>
              <td className="px-4 py-2.5">{fmtDate(r.date)}</td>
              <td className="px-4 py-2.5 hidden sm:table-cell text-slate-600">
                {r.clockIn && new Date(r.clockIn).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </td>
              <td className="px-4 py-2.5 hidden sm:table-cell text-slate-600">
                {r.status === 'open'
                  ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Running</Badge>
                  : (r.clockOut && new Date(r.clockOut).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }))
                }
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums font-medium">{Number(r.hours || 0).toFixed(2)}h</td>
              <td className="px-4 py-2.5 hidden md:table-cell">
                {r.workOrderId
                  ? <Link to={`/admin/work-orders`} className="text-blue-600 hover:underline font-mono text-xs">{r.workOrderId}</Link>
                  : <span className="text-slate-400">—</span>
                }
              </td>
              <td className="px-4 py-2.5 hidden md:table-cell">
                {r.outsideGeofence ? (
                  <span
                    title={r.outsideReason || 'No reason given'}
                    className="inline-flex items-center gap-1 text-xs text-amber-700"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {Math.round(r.distanceMeters || 0)}m off
                  </span>
                ) : r.distanceMeters !== null && r.distanceMeters !== undefined ? (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                    <MapPin className="h-3 w-3" /> on-site
                  </span>
                ) : (
                  <span className="text-xs text-slate-400">no GPS</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const WorkOrderLabourList = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
        <Receipt className="h-8 w-8 mx-auto text-slate-300 mb-2" />
        <div className="text-sm text-slate-500">No labour entries logged on work orders.</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-4 py-2.5">When</th>
            <th className="text-left px-4 py-2.5">Work order</th>
            <th className="text-left px-4 py-2.5 hidden sm:table-cell">Description</th>
            <th className="text-right px-4 py-2.5">Hours</th>
            <th className="text-right px-4 py-2.5 hidden sm:table-cell">£</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i} data-testid={`wo-labour-row-${r.workOrderId}-${r.lineIndex}`}>
              <td className="px-4 py-2.5 text-xs text-slate-600">{fmtDateTime(r.addedAt)}</td>
              <td className="px-4 py-2.5">
                <Link to={`/admin/work-orders`} className="text-blue-600 hover:underline">
                  <span className="font-mono text-xs">{r.workOrderId}</span>
                </Link>
                {r.workOrderTitle && <div className="text-xs text-slate-500 truncate max-w-[180px]">{r.workOrderTitle}</div>}
              </td>
              <td className="px-4 py-2.5 hidden sm:table-cell text-slate-600">
                {r.description}
                {r.sourceTimerId && <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 text-[10px]">timed</Badge>}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums font-medium">{r.hours.toFixed(2)}h</td>
              <td className="px-4 py-2.5 text-right tabular-nums hidden sm:table-cell">{fmtMoney(r.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProfileEditor = ({ editing, setEditing }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Full name" value={editing.name} onChange={v => setEditing({ ...editing, name: v })} testId="ep-name" />
      <Field label="Job title" value={editing.title} onChange={v => setEditing({ ...editing, title: v })} placeholder="e.g. Senior Engineer" />
      <Field label="Email" value={editing.email} onChange={v => setEditing({ ...editing, email: v })} type="email" />
      <Field label="Phone" value={editing.phone} onChange={v => setEditing({ ...editing, phone: v })} />
      <Field
        label="Default hourly rate (£)"
        value={editing.rate}
        onChange={v => setEditing({ ...editing, rate: v })}
        type="number"
        placeholder="35"
      />
    </div>
    <div className="text-xs text-slate-500">
      Rate is informational on this screen — work-order labour entries always use the rate stored on the labour-rate item at time of logging.
    </div>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <Input
      data-testid={testId}
      type={type}
      className="mt-1"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

export default EngineerProfile;
